'use client';

import { MinimizeIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChatContext } from '../../context/chat-context';
import {
  Artifact,
  ArtifactAction,
  ArtifactActions,
  ArtifactContent,
  ArtifactDescription,
  ArtifactHeader,
  ArtifactTitle,
} from '../elements/artifact';
import { getWidgetConfig } from './widget-registry';

interface ToolWidgetRendererProps {
  toolName: string;
  result: any;
  args: any;
}

export function ToolWidgetRenderer({ toolName, result, args }: ToolWidgetRendererProps) {
  const { t } = useTranslation('chat2');
  const { fullscreenWidget, setFullscreenWidget } = useChatContext();
  const isFullscreen =
    fullscreenWidget?.toolName === toolName &&
    JSON.stringify(fullscreenWidget?.result) === JSON.stringify(result);
  const widgetConfig = getWidgetConfig(toolName);
  if (!widgetConfig) {
    return null;
  }
  if (isFullscreen) {
    return (
      <Artifact>
        <ArtifactHeader>
          <div>
            <ArtifactTitle>{widgetConfig.getTitle()}</ArtifactTitle>
            <ArtifactDescription>{t('artifact.viewingFullscreen')}</ArtifactDescription>
          </div>
          <ArtifactActions>
            <ArtifactAction
              icon={MinimizeIcon}
              label={t('artifact.exitFullscreen')}
              tooltip={t('artifact.exitFullscreenTooltip')}
              onClick={() => setFullscreenWidget(null)}
            />
          </ArtifactActions>
        </ArtifactHeader>
      </Artifact>
    );
  }
  const onFullscreen = () => {
    setFullscreenWidget({
      toolName,
      result,
      args,
      title: widgetConfig.getTitle(),
    });
  };
  const actions = widgetConfig.getActions?.(result, args, onFullscreen) ?? [];
  const description = widgetConfig.getDescription?.(result) ?? '';
  return (
    <Artifact>
      <ArtifactHeader>
        <div>
          <ArtifactTitle>{widgetConfig.getTitle()}</ArtifactTitle>
          {description && <ArtifactDescription>{description}</ArtifactDescription>}
        </div>
        <ArtifactActions>{actions}</ArtifactActions>
      </ArtifactHeader>
      <ArtifactContent className="max-h-[350px] overflow-y-auto">
        {widgetConfig.renderNormal(result, args)}
      </ArtifactContent>
    </Artifact>
  );
}
